import { useCallback, useEffect, useState } from 'react'

import type { AdminAuditListItem } from '@study-decks/shared'

import { useAuth } from '../../auth/AuthContext.js'
import { Button, Card } from '../../components'
import { showToast } from '../../components/Toast'
import { AdminApiError, listAdminAudit } from '../../lib/admin-api.js'
import styles from './AuditPage.module.css'

const DATE_FORMATTER = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
})

export default function AuditPage(): JSX.Element {
  const { firebaseUser } = useAuth()
  const [events, setEvents] = useState<AdminAuditListItem[]>([])
  const [nextPageToken, setNextPageToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const getIdToken = useCallback(async () => {
    if (!firebaseUser) throw new Error('Admin session missing')
    return firebaseUser.getIdToken()
  }, [firebaseUser])

  const loadEvents = useCallback(
    async (append: boolean, pageToken?: string | null) => {
      if (!firebaseUser) return
      const setter = append ? setLoadingMore : setLoading
      setter(true)
      if (!append) setErrorMessage(null)

      try {
        const response = await listAdminAudit(getIdToken, {
          pageSize: 25,
          pageToken: pageToken ?? null,
        })
        setEvents((previous) => (append ? [...previous, ...response.events] : response.events))
        setNextPageToken(response.nextPageToken)
      } catch (err) {
        const message =
          err instanceof AdminApiError || err instanceof Error
            ? err.message
            : 'Unable to load audit events right now.'
        setErrorMessage(message)
        showToast(message, 'error')
      } finally {
        setter(false)
      }
    },
    [firebaseUser, getIdToken],
  )

  useEffect(() => {
    void loadEvents(false, null)
  }, [loadEvents])

  return (
    <section className={styles.page}>
      <div className={styles.header}>
        <div>
          <p className={styles.eyebrow}>Privileged history</p>
          <h2>Audit log</h2>
        </div>
        <p className={styles.copy}>
          Every admin mutation lands here so access changes stay visible as Phase 3 teacher
          authoring comes online.
        </p>
      </div>

      <Card elevated className={styles.listCard}>
        {errorMessage && !loading ? (
          <div className={styles.stateBlock}>
            <p role="alert">{errorMessage}</p>
            <Button type="button" variant="secondary" onClick={() => void loadEvents(false, null)}>
              Retry
            </Button>
          </div>
        ) : null}

        {loading ? (
          <div className={styles.stateBlock}>
            <p>Loading audit events…</p>
          </div>
        ) : null}

        {!loading && events.length === 0 && !errorMessage ? (
          <div className={styles.stateBlock}>
            <p>No audit events yet.</p>
          </div>
        ) : null}

        {!loading && events.length > 0 ? (
          <>
            <ol className={styles.timeline}>
              {events.map((event) => (
                <li key={event.id} className={styles.item}>
                  <div className={styles.timestamp}>{formatDate(event.ts)}</div>
                  <div className={styles.details}>
                    <div className={styles.metaRow}>
                      <span className={styles.label}>Actor</span>
                      <span className={styles.value}>{event.actorEmail ?? event.actorId}</span>
                    </div>
                    <div className={styles.metaRow}>
                      <span className={styles.label}>Target</span>
                      <span className={styles.value}>{event.targetEmail ?? event.targetId}</span>
                    </div>
                    <div className={styles.metaRow}>
                      <span className={styles.label}>Action</span>
                      <span className={styles.value}>{event.action}</span>
                    </div>
                    <div className={styles.metaRow}>
                      <span className={styles.label}>Detail</span>
                      <span className={styles.value}>{describeEvent(event)}</span>
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            {nextPageToken ? (
              <div className={styles.loadMore}>
                <Button
                  type="button"
                  variant="secondary"
                  loading={loadingMore}
                  onClick={() => void loadEvents(true, nextPageToken)}
                >
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        ) : null}
      </Card>
    </section>
  )
}

function describeEvent(event: AdminAuditListItem) {
  if (event.action === 'set_role') {
    const role = typeof event.payload['role'] === 'string' ? event.payload['role'] : 'unknown'
    return `Role set to ${role}.`
  }
  if (event.action === 'set_disabled') {
    return event.payload['disabled'] === true ? 'Account disabled.' : 'Account enabled.'
  }
  if (event.action === 'delete_deck') {
    const title = typeof event.payload['deckTitle'] === 'string' ? event.payload['deckTitle'] : null
    const ownerRole =
      typeof event.payload['ownerRole'] === 'string' ? event.payload['ownerRole'] : null
    const prefix = ownerRole ? `${ownerRole} deck` : 'Deck'
    return title ? `${prefix} "${title}" deleted.` : `${prefix} deleted.`
  }
  if (event.action === 'flag_deck') {
    const reason = typeof event.payload['reason'] === 'string' ? event.payload['reason'] : null
    return reason ? `Deck flagged: ${reason}` : 'Deck flagged for review.'
  }
  if (event.action === 'moderate_deck') {
    const action = typeof event.payload['action'] === 'string' ? event.payload['action'] : null
    return action ? `Deck moderated: ${action}.` : 'Deck moderation recorded.'
  }
  return 'Admin update recorded.'
}

function formatDate(value: string) {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return DATE_FORMATTER.format(parsed)
}
